import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import DemoWidget from '../components/DemoWidget'

interface Comparison {
  query: string
  chatgpt: string
  gpmeta: string
  source: string
}

const COMPARISONS: Comparison[] = [
  {
    query: 'PBS authority criteria for empagliflozin in T2DM with established CV disease',
    chatgpt:
      'Empagliflozin is generally subsidised for adults with type 2 diabetes who require additional glycaemic control. Authority may be required — consult the PBS website for current criteria.',
    gpmeta:
      'According to PBS: Authority Required (Streamlined). Patient must have T2DM, established cardiovascular disease, and HbA1c >7% on metformin ± sulfonylurea. Item 11789N (10mg) / 11790P (25mg).',
    source: 'PBS',
  },
  {
    query: 'First-line antibiotic for community-acquired pneumonia, mild, otherwise well adult',
    chatgpt:
      'For mild community-acquired pneumonia in healthy adults, amoxicillin or doxycycline are commonly used. Some guidelines suggest azithromycin. Local resistance patterns may apply.',
    gpmeta:
      'According to eTG: amoxicillin 1 g orally, 8-hourly for 5 days. If hypersensitivity to penicillin (non-severe): doxycycline 100 mg orally, 12-hourly for 5 days.',
    source: 'eTG',
  },
  {
    query: 'MBS item for 30-minute mental health treatment plan with a GP',
    chatgpt:
      'For mental health consultations, GPs can use items such as 2715 or 2717. Item 36 may also apply for longer consults. Refer to MBS Online for current rebates.',
    gpmeta:
      'According to MBS: Item 2715 — GP Mental Health Treatment Plan, ≥20 min, GP not undertaken Mental Health Skills Training. Item 2717 if ≥40 min and skills-trained. Not Item 36 (general consult).',
    source: 'MBS',
  },
]

interface FAQ {
  q: string
  a: string
}

const FAQS: FAQ[] = [
  {
    q: 'Is GPMeta a medical device or clinical decision support tool?',
    a: 'No. GPMeta is a search and synthesis tool over publicly available Australian clinical references. It does not diagnose, prescribe, or make autonomous clinical decisions. It is not Software as a Medical Device under TGA definitions.',
  },
  {
    q: "Does GPMeta align with AHPRA's AI guidance?",
    a: "AHPRA's 2024 guidance places obligations on practitioners — accountability, understanding the tool, transparency, informed consent, and data governance. GPMeta is designed to make those obligations easy to meet: every answer cites a verified Australian source, scope and limitations are documented publicly, and no patient data is required to use the tool.",
  },
  {
    q: 'Will my MDO have a problem with this?',
    a: 'GPMeta works the same way as searching eTG, RACGP guidelines, or AMH directly — reference, not advice. We are engaging Australian MDOs proactively. If your MDO has questions, contact us at hello@gpmeta.au and we will respond on your behalf.',
  },
  {
    q: 'Can I paste patient details into a query?',
    a: 'No. Do not paste identifying patient information into any AI tool, including GPMeta. Frame queries clinically: "65-year-old, eGFR 35, T2DM, optimal SGLT2?" — never names, MRNs, or DOBs. We do not log query content, but the principle stands regardless.',
  },
  {
    q: 'How is this different from ChatGPT or Claude?',
    a: 'ChatGPT is trained on the global internet and defaults to American drug names, NICE guidelines, and confidently invents PBS criteria when asked. GPMeta is grounded in Australian sources only, forces a citation on every answer, and refuses to answer when sources do not cover the question.',
  },
  {
    q: 'Does GPMeta replace clinical judgement?',
    a: 'No. GPMeta surfaces what trusted Australian sources say. You apply judgement to the patient in front of you. Same as reading eTG — faster.',
  },
  {
    q: 'What if a guideline changes?',
    a: 'Sources are reindexed regularly. Primary-source links are included on every answer so you can verify currency directly against eTG, RACGP, PBS, or MBS.',
  },
  {
    q: 'Why not free? ChatGPT is free.',
    a: 'Per-query API costs, Australian source curation, hosting, and ongoing maintenance. Built sustainably so it is still here in five years. $25/mo is tax-deductible — about one Level B consult item per year.',
  },
  {
    q: 'Is my data safe?',
    a: 'Query content is not logged. Inputs are never used to train models. Hosted on Cloudflare\'s Australian edge. Authentication via Supabase with row-level security. The full privacy spec is public.',
  },
  {
    q: 'Can I cancel?',
    a: 'Anytime, one click. Australian consumer law applies — refunds where applicable.',
  },
  {
    q: 'Are you a GP?',
    a: 'Yes. Dr James Condon, Adelaide — RACGP registrar. Built GPMeta because mid-consult tab juggling across eTG, PBS, MBS, RACGP and state guidelines was slowing care.',
  },
  {
    q: 'Is GPMeta endorsed by RACGP or AHPRA?',
    a: 'No. Endorsement is not required to operate, and we do not claim it. GPMeta is designed to support practitioners meeting their existing professional obligations.',
  },
]

const AHPRA_OBLIGATIONS: { obligation: string; how: string }[] = [
  {
    obligation: 'Accountability — your judgement remains primary',
    how: 'Every answer is descriptive ("eTG states..."), never prescriptive. The clinician decides.',
  },
  {
    obligation: 'Understanding the tool',
    how: 'Public scope, source list, training data, and known limitations. Audit-ready.',
  },
  {
    obligation: 'Transparency with patients',
    how: 'GPMeta is a reference search tool — comparable to consulting eTG mid-consult. No patient data involved when used as intended.',
  },
  {
    obligation: 'Informed consent',
    how: 'No patient identifiers are required. Same posture as searching a guideline directly.',
  },
  {
    obligation: 'Data governance and privacy',
    how: 'Query content not logged. Inputs not used for training. Australian edge hosting. Privacy spec public.',
  },
]

const SOURCES = [
  'eTG Complete',
  'RACGP',
  'PBS',
  'MBS',
  'AMH',
  'NPS MedicineWise',
  'ASHM',
  'Healthdirect',
  'TGA',
  'AIHW',
  'PubMed',
  'State Health',
]

export default function Landing() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/app', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  const goToSignup = () => navigate('/login', { state: { signup: true } })

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <a href="/" className="text-xl font-bold">GPMeta</a>
          <div className="hidden md:flex items-center gap-6 text-sm text-gray-600">
            <a href="#compare" className="hover:text-gray-900">vs ChatGPT</a>
            <a href="#ahpra" className="hover:text-gray-900">AHPRA posture</a>
            <a href="#pricing" className="hover:text-gray-900">Pricing</a>
            <a href="#faq" className="hover:text-gray-900">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-gray-700 hover:text-gray-900 font-medium px-3 py-2"
            >
              Log in
            </button>
            <button
              onClick={goToSignup}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700"
            >
              Start trial
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-3">
              AHPRA-verified GPs only
            </p>
            <h1 className="text-4xl md:text-6xl font-bold mb-5">
              Every Australian clinical resource. One search.
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
              eTG, RACGP, PBS, MBS, AMH, ASHM, state guidelines — cited on every answer.
              Australian guidelines, Australian drug names, Australian billing. Built for Clinic Mode.
            </p>
          </div>

          <DemoWidget />

          <p className="text-center text-sm text-gray-600 mt-6">
            Try 2 demo queries. No signup. No card.
          </p>
        </div>
      </section>

      {/* Why not ChatGPT */}
      <section id="compare" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why not just use ChatGPT?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              ChatGPT was trained on the internet. GPMeta is grounded in Australian clinical sources
              and forces a citation on every answer. Three real queries, side by side.
            </p>
          </div>

          <div className="space-y-8">
            {COMPARISONS.map((c, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Query
                  </p>
                  <p className="text-base md:text-lg font-medium text-gray-900 mt-1">
                    {c.query}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200 bg-red-50/30">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-1 rounded">
                        ChatGPT
                      </span>
                      <span className="text-xs text-gray-500">general internet</span>
                    </div>
                    <p className="text-sm text-gray-800 leading-relaxed">{c.chatgpt}</p>
                  </div>
                  <div className="p-6 bg-green-50/30">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded">
                        GPMeta
                      </span>
                      <span className="text-xs text-gray-500">cites {c.source}</span>
                    </div>
                    <p className="text-sm text-gray-800 leading-relaxed">{c.gpmeta}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8 italic">
            Comparisons illustrative — verify against primary sources before clinical use.
          </p>
        </div>
      </section>

      {/* Three concerns */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            What GPs ask first
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <p className="text-sm font-semibold text-gray-500 mb-2">"ChatGPT works fine for me."</p>
              <p className="text-gray-800 leading-relaxed">
                Until it confidently cites a NICE guideline that does not apply, or invents a PBS
                criterion. GPMeta is grounded in Australian sources and forces a citation on every
                answer. No hallucinated guidelines.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <p className="text-sm font-semibold text-gray-500 mb-2">"I do not want AHPRA scrutiny."</p>
              <p className="text-gray-800 leading-relaxed">
                AHPRA's AI guidance places five obligations on the practitioner, not the tool.
                GPMeta gives you the documentation AHPRA expects: scope, intended use, limitations,
                source list. ChatGPT cannot.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <p className="text-sm font-semibold text-gray-500 mb-2">"What about patient privacy?"</p>
              <p className="text-gray-800 leading-relaxed">
                Do not paste identifying patient details into any AI tool — including GPMeta.
                Query content is not logged, hosted on Cloudflare's Australian edge, and inputs
                never train models. Architecture, not promises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How it works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-blue-600 text-4xl font-bold mb-3">1</div>
              <h3 className="text-lg font-semibold mb-2">Type a clinical question</h3>
              <p className="text-gray-700 text-sm">
                Plain language. No prompt engineering. Frame clinically — never with patient
                identifiers.
              </p>
            </div>
            <div>
              <div className="text-blue-600 text-4xl font-bold mb-3">2</div>
              <h3 className="text-lg font-semibold mb-2">GPMeta searches Australian sources</h3>
              <p className="text-gray-700 text-sm">
                eTG, RACGP, PBS, MBS, AMH, ASHM, your state's health pathways. Filtered by your
                location and practice context.
              </p>
            </div>
            <div>
              <div className="text-blue-600 text-4xl font-bold mb-3">3</div>
              <h3 className="text-lg font-semibold mb-2">One sentence. Cited. Under 3 seconds.</h3>
              <p className="text-gray-700 text-sm">
                Clinic Mode: 1–2 sentences with primary citation. Study Mode: up to 500 words for
                evening reading.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            Indexed Australian sources
          </h2>
          <p className="text-center text-gray-600 mb-10">We index. We cite. We do not replace.</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {SOURCES.map((s) => (
              <div
                key={s}
                className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 text-center"
              >
                <p className="text-sm font-semibold text-gray-700">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AHPRA posture */}
      <section id="ahpra" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Built around your AHPRA obligations
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              AHPRA's 2024 guidance places five obligations on the practitioner. GPMeta is designed
              to make each one easy to meet.
            </p>
          </div>

          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 bg-gray-50 px-6 py-3 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Your obligation
              </p>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:block">
                How GPMeta supports it
              </p>
            </div>
            {AHPRA_OBLIGATIONS.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-1 md:grid-cols-2 px-6 py-5 ${
                  i % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'
                } ${i < AHPRA_OBLIGATIONS.length - 1 ? 'border-b border-gray-200' : ''}`}
              >
                <p className="font-medium text-gray-900 mb-2 md:mb-0">{row.obligation}</p>
                <p className="text-gray-700 text-sm leading-relaxed">{row.how}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            Reference:{' '}
            <a
              href="https://www.ahpra.gov.au/Resources/Artificial-Intelligence-in-healthcare.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-700"
            >
              AHPRA — Meeting your professional obligations when using AI in healthcare
            </a>
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Pricing</h2>
            <p className="text-lg text-gray-700">
              Tax-deductible professional expense. Cancel anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Trial */}
            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold mb-2">Free trial</h3>
              <div className="text-4xl font-bold text-blue-600 mb-1">14 days</div>
              <p className="text-gray-600 mb-6 text-sm">card required, 50 queries</p>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>Full source access</li>
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>Clinic + Study mode</li>
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>AHPRA verification required</li>
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>One-click cancel</li>
              </ul>
              <button
                onClick={goToSignup}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700"
              >
                Start trial
              </button>
            </div>

            {/* Founding */}
            <div className="bg-blue-600 text-white rounded-xl p-8 border-2 border-blue-700 relative shadow-lg md:scale-105">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span className="bg-amber-400 text-amber-900 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                  First 100 GPs
                </span>
              </div>
              <h3 className="text-xl font-bold mb-2 mt-2">Founding member</h3>
              <div className="text-4xl font-bold mb-1">$200<span className="text-lg font-normal">/yr</span></div>
              <p className="text-blue-100 mb-1 text-sm">≈ $16.67/mo, locked lifetime</p>
              <p className="text-blue-200 text-xs mb-6">~$11/mo after tax deduction</p>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-start"><span className="text-amber-300 mr-2">✓</span>Lifetime founder pricing</li>
                <li className="flex items-start"><span className="text-amber-300 mr-2">✓</span>All sources, all modes</li>
                <li className="flex items-start"><span className="text-amber-300 mr-2">✓</span>Direct line to founder</li>
                <li className="flex items-start"><span className="text-amber-300 mr-2">✓</span>Vote on roadmap</li>
              </ul>
              <button
                onClick={goToSignup}
                className="w-full bg-white text-blue-700 py-3 px-6 rounded-lg font-semibold hover:bg-blue-50"
              >
                Claim founding spot
              </button>
            </div>

            {/* Standard */}
            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold mb-2">Individual</h3>
              <div className="text-4xl font-bold text-blue-600 mb-1">$25<span className="text-lg font-normal text-gray-600">/mo</span></div>
              <p className="text-gray-600 mb-1 text-sm">or $250/yr (save $50)</p>
              <p className="text-gray-500 text-xs mb-6">~$14/mo after tax deduction</p>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>All sources, all modes</li>
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>25 queries/day</li>
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>Top-up packs available</li>
                <li className="flex items-start"><span className="text-green-500 mr-2">✓</span>Tax-deductible invoice</li>
              </ul>
              <button
                onClick={goToSignup}
                className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800"
              >
                Choose individual
              </button>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-10">
            Practice and clinic plans coming. Contact{' '}
            <a href="mailto:hello@gpmeta.au" className="underline">hello@gpmeta.au</a>.
          </p>
        </div>
      </section>

      {/* Founder note */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Built by a working GP</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            I am Dr James Condon, a GP and RACGP registrar in Adelaide. I built GPMeta because
            mid-consult I was juggling 15 tabs across eTG, PBS, MBS, RACGP and SA Health — and the
            patient was waiting.
          </p>
          <p className="text-gray-700 leading-relaxed">
            GPMeta is one search box. Australian sources only. Cited every time. Designed to make
            the next three minutes faster, not to replace the clinician's judgement.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Frequently asked
          </h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const open = openFaq === i
              return (
                <div
                  key={i}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-gray-50"
                    aria-expanded={open}
                  >
                    <span className="font-medium text-gray-900">{f.q}</span>
                    <span className="text-gray-400 text-xl ml-4 flex-shrink-0">
                      {open ? '−' : '+'}
                    </span>
                  </button>
                  {open && (
                    <div className="px-5 pb-5 text-gray-700 text-sm leading-relaxed">{f.a}</div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            14 days. 50 queries. AHPRA-verified GPs only.
          </h2>
          <p className="text-blue-100 mb-8">
            Card required so we can keep founder costs sustainable. Cancel one click. Founding 100
            spots locked at $200/yr lifetime.
          </p>
          <button
            onClick={goToSignup}
            className="bg-white text-blue-700 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50"
          >
            Start your trial
          </button>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-xl font-bold mb-3">GPMeta</h3>
              <p className="text-gray-400 mb-4 text-sm">
                Every Australian clinical resource. One search.
              </p>
              <p className="text-amber-400 font-semibold text-sm mb-1">
                For AHPRA-registered GPs only
              </p>
              <p className="text-gray-400 text-xs">
                Not a medical device. Not clinical decision support. Not a substitute for clinical
                judgement.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-sm">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/terms" className="text-gray-400 hover:text-white">Terms</a></li>
                <li><a href="/privacy" className="text-gray-400 hover:text-white">Privacy</a></li>
                <li><a href="/disclaimer" className="text-gray-400 hover:text-white">Disclaimer</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-sm">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>hello@gpmeta.au</li>
                <li>Adelaide, Australia</li>
                <li>ABN: 52 658 820 449</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-10 pt-6 text-center">
            <p className="text-gray-500 text-xs">© 2026 GPMeta. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
